import type { ExerciseAnswerInput, RunStartInput } from "@calega/content";
import type { LearnerRunHistory } from "@calega/exercise-engine";
import { api, ApiError } from "../lib/api";
import { requestReflection } from "./reflection-request";
import { deliverAnswer, hasUncertainAnswer, waitForPendingAnswers } from "./answer-delivery";

export const exerciseRunStartedEvent = "calepia:exercise-run-started";
export const exerciseRunEndedEvent = "calepia:exercise-run-ended";

export function getCompletedRunHistory(runId: string, signal: AbortSignal) {
  return api<LearnerRunHistory>(`/learning/runs/${encodeURIComponent(runId)}/review`, { signal });
}

export function getExerciseRunStatus(runId: string, signal: AbortSignal) {
  return api<{ status: string; abortReason: string | null }>(`/learning/runs/${encodeURIComponent(runId)}/status`, { signal });
}

function notifyExerciseRunStarted(response: unknown) {
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(exerciseRunStartedEvent, { detail: response }));
}

function notifyExerciseRunEnded(runId: string, abortReason?: string | null) {
  for (const key of partialBatches.keys()) if (key.startsWith(`${runId}:`)) partialBatches.delete(key);
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(exerciseRunEndedEvent, { detail: { runId, abortReason } }));
}

async function reportClosedRun(runId: string, error: unknown): Promise<never> {
  if (error instanceof ApiError && (error.status === 400 || error.status === 409)) {
    const result = await api<{ status: string; abortReason: string | null }>(`/learning/runs/${encodeURIComponent(runId)}/status`, { signal: AbortSignal.timeout(12_000) }).catch(() => null);
    if (result?.status === "ABORTED" && result.abortReason) notifyExerciseRunEnded(runId, result.abortReason);
  }
  throw error;
}

export interface ExerciseRunScore {
  score: number;
  maxScore: number;
}

export async function startExerciseRun<Response = { runId: string; exercise: unknown }>(input: RunStartInput) {
  const response = await api<Response>("/learning/runs", {
    method: "POST",
    body: JSON.stringify(input),
  });
  notifyExerciseRunStarted(response);
  return response;
}

export async function startAdaptiveAssignmentRun<Response = { runId: string; exercise: unknown }>(assignmentId: string) {
  const response = await api<Response>(`/learning/adaptive-assignments/${encodeURIComponent(assignmentId)}/runs`, { method: "POST" });
  notifyExerciseRunStarted(response);
  return response;
}

export async function startIndividualAssignmentRun<Response = { runId: string; exercise: unknown }>(assignmentId: string, settings: unknown) {
  const response = await api<Response>(`/learning/individual-assignments/${encodeURIComponent(assignmentId)}/runs`, {
    method: "POST",
    body: JSON.stringify({ settings }),
  });
  notifyExerciseRunStarted(response);
  return response;
}

export function submitSkippedExerciseAnswer(runId: string, itemId: string, type: "mathe" | "einmaleins") {
  return submitExerciseAnswer(runId, { type, itemId, value: null, skipped: true, correctionCount: 0, intermediateValues: [] });
}

const pendingChecks = new Map<string, Set<Promise<unknown>>>();
const partialBatches = new Map<string, Map<number, unknown>>();
const inFlightBatches = new Map<string, Promise<unknown[]>>();
export function submitExerciseAnswers<Response = unknown>(runId: string, inputs: ExerciseAnswerInput[]): Promise<Response[]> {
  const batchKey = `${runId}:${JSON.stringify(inputs)}`;
  const existing = inFlightBatches.get(batchKey);
  if (existing) return existing as Promise<Response[]>;
  const saved = partialBatches.get(batchKey) ?? new Map<number, unknown>();
  const submission = (async () => {
    if (!partialBatches.has(batchKey) && partialBatches.size >= 64) throw new Error("Zu viele unbestätigte Abgaben. Bitte zunächst die Verbindung wiederherstellen.");
    partialBatches.set(batchKey, saved);
    const unsaved = inputs.filter((_input, index) => !saved.has(index));
    const reflection = unsaved.length > 0 && unsaved.every((input) => hasUncertainAnswer(runId, input)) ? undefined : requestReflection(runId, inputs);
    if (reflection) await reflection;
    const results: Response[] = [];
    for (const [index, input] of inputs.entries()) {
      if (!saved.has(index)) saved.set(index, await deliverAnswer<Response>(runId, input).catch((error) => reportClosedRun(runId, error)));
      results.push(saved.get(index) as Response);
    }
    partialBatches.delete(batchKey);
    if (inputs.length && typeof window !== "undefined") window.dispatchEvent(new CustomEvent("calepia:reflection-delivered", { detail: { runId, inputs } }));
    return results;
  })();
  const checks = pendingChecks.get(runId) ?? new Set(); checks.add(submission); pendingChecks.set(runId, checks);
  const result = submission.finally(() => { inFlightBatches.delete(batchKey); if (!saved.size) partialBatches.delete(batchKey); checks.delete(submission); if (!checks.size) pendingChecks.delete(runId); });
  inFlightBatches.set(batchKey, result);
  return result;
}

export async function submitExerciseAnswer<Response = unknown>(runId: string, input: ExerciseAnswerInput) {
  return (await submitExerciseAnswers<Response>(runId, [input]))[0]!;
}

async function saveRunCompletion<Response>(path: string): Promise<Response> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      // The server locks the run and returns its existing result on a repeated finish.
      return await api<Response>(path, { method: "POST", signal: AbortSignal.timeout(12_000) });
    } catch (error) {
      if (error instanceof ApiError && error.status < 500 && error.status !== 429) throw error;
      if (attempt === 2) throw new Error("Der Abschluss wurde noch nicht bestätigt. Bitte erneut speichern; das Ergebnis wird nicht doppelt gespeichert.");
      await new Promise((resolve) => setTimeout(resolve, error instanceof ApiError && error.status === 429 ? 12_000 : 1_000 * (attempt + 1)));
    }
  }
}

export async function finishExerciseRun<Response = ExerciseRunScore>(runId: string) {
  await Promise.all([...(pendingChecks.get(runId) ?? [])]);
  await waitForPendingAnswers(runId);
  const path = `/learning/runs/${encodeURIComponent(runId)}/finish`;
  let result: Response;
  try {
    result = await saveRunCompletion<Response>(path);
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 409 || !error.message.startsWith("Ausstehende Antworten")) {
      return reportClosedRun(runId, error);
    }
    await new Promise((resolve) => setTimeout(resolve, 60_000));
    await waitForPendingAnswers(runId);
    result = await saveRunCompletion<Response>(path).catch((retryError) => reportClosedRun(runId, retryError));
  }
  notifyExerciseRunEnded(runId);
  return result;
}

export async function abortExerciseRun(runId: string) {
  const result = await api(`/learning/runs/${encodeURIComponent(runId)}/abort`, { method: "POST" });
  notifyExerciseRunEnded(runId);
  return result;
}

export async function startFlashcardRun(deckId: string) {
  const response = await api<{ runId: string; pausedMs?: number; pauseStartedAt?: string | null }>(`/learning/flashcards/${encodeURIComponent(deckId)}/runs`, { method: "POST" });
  notifyExerciseRunStarted(response);
  return response;
}
